import { isHiddenReportColumn } from './dashboardPortability';

/** True when SQL is safe to run on the read-only query endpoint (SELECT / WITH / TABLE). */
export function isReadOnlyQuery(sql) {
    const trimmed = (sql || '').trim();
    if (!trimmed) return false;
    return /^(SELECT|WITH|TABLE)\b/i.test(trimmed);
}

/** Strip leading SQL line/block comments and blank lines. */
export function stripLeadingSqlComments(sql) {
    let s = (sql || '').trim();
    for (;;) {
        if (!s) break;
        if (/^--[^\r\n]*/m.test(s) && s.match(/^--/)) {
            s = s.replace(/^--[^\r\n]*\r?\n?/, '').trimStart();
            continue;
        }
        if (/^\/\*/.test(s)) {
            s = s.replace(/^\/\*[\s\S]*?\*\//, '').trimStart();
            continue;
        }
        break;
    }
    return s.trim();
}

/** True when SQL looks like CREATE/DROP FUNCTION DDL (belongs on Function tab, not Query tab). */
export function isFunctionDdl(sql) {
    const trimmed = stripLeadingSqlComments(sql);
    if (!trimmed) return false;
    return /^(CREATE\s+(OR\s+REPLACE\s+)?FUNCTION|DROP\s+FUNCTION)/i.test(trimmed);
}

/** Remove a trailing statement terminator (invalid inside subquery wrappers). */
export function stripTrailingSemicolon(sql) {
    return (sql || '').trim().replace(/;\s*$/, '');
}

/**
 * Replace function parameters with literals for raw-query preview.
 * Bare SELECT runs outside the function, so unbound p_* names are treated as missing columns (42703).
 */
export function substituteFunctionParamsForPreview(sql) {
    let s = stripTrailingSemicolon(sql);
    s = s.replace(/\bp_date_from\b/gi, "(NOW() - INTERVAL '3 months')");
    s = s.replace(/\bp_date_to\b/gi, 'NOW()');
    // Optional filters: NULL so (param IS NULL OR …) branches match “no filter”.
    s = s.replace(/\bp_organisation_id\b/gi, 'NULL::text');
    s = s.replace(/\bp_cohort_id\b/gi, 'NULL::uuid');
    s = s.replace(/\bp_upload_batch_id\b/gi, 'NULL::uuid');
    return s;
}

const SAFE_IDENT = name => (name || '').replace(/[^\w]/g, '_') || 'col';

/**
 * Build CREATE OR REPLACE FUNCTION wrapping a tested SELECT (aggregate_report shape).
 * Replaces literal NOW() date ranges with p_date_from / p_date_to when present.
 */
/** Rewrite CREATE FUNCTION target name (schema.function_name). */
export function replaceFunctionNameInDdl(sql, schema = 'public', functionName = 'aggregate_report') {
    const safeSchema = SAFE_IDENT(schema) || 'public';
    const safeName = SAFE_IDENT(functionName) || 'aggregate_report';
    return (sql || '').replace(
        /CREATE\s+(?:OR\s+REPLACE\s+)?FUNCTION\s+(?:"?)(\w+)("?)\.(?:"?)(\w+)("?)\s*\(/i,
        `CREATE OR REPLACE FUNCTION ${safeSchema}.${safeName}(`,
    );
}

export function buildCreateFunctionFromSelect(selectSql, schema = 'public', functionName = 'aggregate_report') {
    const inner = stripTrailingSemicolon(selectSql);
    const innerWithParams = inner.replace(
        /BETWEEN\s+NOW\s*\(\s*\)\s*-\s*INTERVAL\s+'[^']+'\s*AND\s+NOW\s*\(\s*\)/gi,
        'BETWEEN p_date_from AND p_date_to',
    );
    const indented = innerWithParams
        .split('\n')
        .map(line => (line.trim() ? `    ${line}` : line))
        .join('\n');

    const safeSchema = SAFE_IDENT(schema) || 'public';
    const safeName = SAFE_IDENT(functionName) || 'aggregate_report';
    const returnsClause = inferReturnsTableClause(innerWithParams);

    return `CREATE OR REPLACE FUNCTION ${safeSchema}.${safeName}(
    p_date_from timestamptz DEFAULT NOW() - INTERVAL '3 months',
    p_date_to   timestamptz DEFAULT NOW(),
    p_cohort_id uuid DEFAULT NULL,
    p_upload_batch_id uuid DEFAULT NULL,
    p_organisation_id text DEFAULT NULL
)
RETURNS TABLE (${returnsClause}) AS $$
BEGIN
    RETURN QUERY
${indented};
END;
$$ LANGUAGE plpgsql STABLE;`;
}

function inferReturnsTableClause(selectSql) {
    if (/\bAS\s+category\b/i.test(selectSql) && /\bAS\s+total_count\b/i.test(selectSql)) {
        return 'category text, total_count bigint';
    }
    if (/\bcategory\b/i.test(selectSql) && /\btotal_count\b/i.test(selectSql)) {
        return 'category text, total_count bigint';
    }
    return 'category text, total_count bigint';
}

/** Suggest chart encoding from report result columns. */
export function inferChartEncoding(columns = []) {
    const visible = columns.filter(c => !isHiddenReportColumn(c.key));
    if (!visible.length) return {};
    const keys = new Set(visible.map(c => c.key));
    if (keys.has('parent_key') && keys.has('node_key')) {
        const encoding = {
            parent: 'parent_key',
            id: 'node_key',
            category: keys.has('label') ? 'label' : 'node_key',
        };
        const valKey = visible.find(c => c.key === 'total_count')?.key
            ?? visible.find(c => c.type === 'number')?.key;
        if (valKey) encoding.value = valKey;
        return encoding;
    }
    const cat =
        visible.find(c => c.key === 'category')?.key
        ?? visible.find(c => c.key === 'period')?.key
        ?? visible.find(c => c.type === 'category' || c.type === 'text' || c.type === 'datetime')?.key
        ?? visible[0]?.key;
    const nums = visible.filter(c => c.type === 'number').map(c => c.key);
    const valKey =
        visible.find(c => c.key === 'total_count')?.key
        ?? visible.find(c => c.key === 'record_count')?.key
        ?? nums[0];
    const encoding = {};
    if (cat) encoding.category = cat;
    if (nums.length === 1) encoding.value = nums[0];
    else if (nums.length > 1) encoding.value = nums;
    return encoding;
}

/**
 * Pull the SELECT (or WITH … SELECT) after RETURN QUERY from a CREATE FUNCTION body.
 * Returns null if nothing usable was found.
 */
export function extractSelectFromFunction(sql) {
    if (!sql?.trim()) return null;

    const cleaned = sql.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/--[^\r\n]*/g, ' ');

    // RETURN QUERY <select> … until END (semicolon before END is optional in StoredFunctions)
    const returnQuery = cleaned.match(
        /RETURN\s+QUERY\s+([\s\S]+?)(?=\s*(?:;\s*)?(?:\r?\n\s*)?END\b)/i,
    );
    if (returnQuery?.[1]) {
        const inner = stripTrailingSemicolon(returnQuery[1].trim());
        if (isReadOnlyQuery(inner)) return inner;
    }

    // Already a bare SELECT/WITH
    if (isReadOnlyQuery(cleaned)) return stripTrailingSemicolon(cleaned.trim());

    return null;
}
